import React, { useState } from 'react';
import { Button, Pagination, Tooltip, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import styled from 'styled-components/macro';

import { useListOwnershipTypesQuery } from '../../../graphql/ownership.generated';
import { OwnershipBuilderModal } from './OwnershipBuilderModal';
import TabToolbar from '../shared/components/styled/TabToolbar';
import { OwnershipTable } from './table/OwnershipTable';
import { OwnershipTypeEntity } from '../../../types.generated';
import { SearchBar } from '../../search/SearchBar';
import { useEntityRegistry } from '../../useEntityRegistry';
import { scrollToTop } from '../../shared/searchUtils';
import { ComponentSpinner } from '../../shared/ComponentSpinner';
import { useTranslation } from '../../../i18n/useTranslation';
import { OWNERSHIP_DISPLAY_TYPES } from '../shared/containers/profile/sidebar/Ownership/ownershipUtils';
import { useRbac } from '../shared/useRbac';

type OwnershipTypeWithLegacy = OwnershipTypeEntity & { isLegacy?: boolean };

const NONE_URN = 'urn:li:ownershipType:none';
const OTHER_URN = 'urn:li:ownershipType:other';

// Convert legacy ownership types to OwnershipTypeEntity format
const legacyOwnershipTypes: OwnershipTypeWithLegacy[] = OWNERSHIP_DISPLAY_TYPES.reduce<OwnershipTypeWithLegacy[]>(
    (acc, legacyType) => {
        if (legacyType.urn !== OTHER_URN) {
            acc.push({
                urn: legacyType.urn,
                type: 'CUSTOM_OWNERSHIP_TYPE' as any,
                info: {
                    name: legacyType.info.name,
                    description: legacyType.info.description,
                },
                isLegacy: true,
            });
        }
        return acc;
    },
    [],
);

const PaginationContainer = styled.div`
    display: flex;
    justify-content: center;
`;

const StyledPagination = styled(Pagination)`
    margin: 40px;
`;

const searchBarStyle = {
    maxWidth: 220,
    padding: 0,
};

const searchBarInputStyle = {
    height: 32,
    fontSize: 14,
};

/**
 * This component renders a paginated, searchable list of Ownership Types.
 */
export const OwnershipList = () => {
    /**
     * Context
     */
    const entityRegistry = useEntityRegistry();
    const { t } = useTranslation();
    const { resolveOwnershipTypePrivileges } = useRbac();
    const { canCreate } = resolveOwnershipTypePrivileges();
    const unauthorisedTooltip = t('UNAUTHORISED_TOOLTIP');
    /**
     * State
     */
    const [page, setPage] = useState(1);
    const [showOwnershipBuilder, setShowOwnershipBuilder] = useState<boolean>(false);
    const [ownershipType, setOwnershipType] = useState<undefined | OwnershipTypeEntity>(undefined);
    const [query, setQuery] = useState<undefined | string>(undefined);

    /**
     * Queries
     */
    const pageSize = 10;
    const start: number = (page - 1) * pageSize;
    const { data, loading, error, refetch } = useListOwnershipTypesQuery({
        variables: {
            input: {
                start,
                count: pageSize,
                query,
            },
        },
    });
    const totalOwnershipTypes = data?.listOwnershipTypes?.total || 0;
    const customOwnershipTypes: OwnershipTypeWithLegacy[] =
        data?.listOwnershipTypes?.ownershipTypes.reduce<OwnershipTypeWithLegacy[]>((acc, type) => {
            if (type.urn !== NONE_URN) {
                acc.push({ ...type, isLegacy: false });
            }
            return acc;
        }, []) || [];

    // Merge custom ownership types with legacy types
    const ownershipTypes: OwnershipTypeWithLegacy[] = [...customOwnershipTypes, ...legacyOwnershipTypes];

    const onClickCreateOwnershipType = () => {
        setShowOwnershipBuilder(true);
    };

    const onCloseModal = () => {
        setShowOwnershipBuilder(false);
        setOwnershipType(undefined);
    };

    const onChangePage = (newPage: number) => {
        scrollToTop();
        setPage(newPage);
    };

    return (
        <>
            {error &&
                message.error({
                    content: t('Failed to load Ownership Types! An unexpected error occurred.'),
                    duration: 3,
                })}
            <TabToolbar>
                <Tooltip title={!canCreate ? unauthorisedTooltip : undefined} overlayClassName="f-tooltip-white">
                    <span>
                        <Button
                            type="text"
                            disabled={!canCreate}
                            onClick={onClickCreateOwnershipType}
                            aria-label={t('Create new Ownership Type')}
                        >
                            <PlusOutlined aria-hidden="true" /> {t('Create new Ownership Type')}
                        </Button>
                    </span>
                </Tooltip>
                <SearchBar
                    initialQuery=""
                    placeholderText={t('Search by Name')}
                    suggestions={[]}
                    style={searchBarStyle}
                    inputStyle={searchBarInputStyle}
                    onSearch={() => null}
                    onQueryChange={(q) => {
                        setQuery(q.length > 0 ? q : undefined);
                        setPage(1);
                    }}
                    entityRegistry={entityRegistry}
                />
            </TabToolbar>
            {!data && loading && <ComponentSpinner />}
            <OwnershipTable
                ownershipTypes={ownershipTypes}
                setIsOpen={setShowOwnershipBuilder}
                setOwnershipType={setOwnershipType}
                refetch={refetch}
            />
            {totalOwnershipTypes >= pageSize && (
                <PaginationContainer>
                    <StyledPagination
                        current={page}
                        pageSize={pageSize}
                        total={totalOwnershipTypes}
                        showLessItems
                        onChange={onChangePage}
                        showSizeChanger={false}
                    />
                </PaginationContainer>
            )}
            <OwnershipBuilderModal
                isOpen={showOwnershipBuilder}
                onClose={onCloseModal}
                refetch={refetch}
                ownershipType={ownershipType}
            />
        </>
    );
};
